import { useEffect, useRef, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { RefObject } from 'react';

import { valibotResolver } from '@hookform/resolvers/valibot';

import { useSelectLoginState } from 'Auth/data';
import { LoginState } from 'Auth/data/types';
import type { ILanguage } from 'Consts/language';
import { appDefaultLanguageCode, supportedLanguages } from 'Consts/language';
import { useAppDispatch, useAppSelector } from 'Store';
import { saveLanguage } from 'User/data/async-thunks';

// TODO: add parameter schema type
export const useAppForm = <T extends FieldValues>(schema, defaultValues?) =>
  useForm<T>({
    mode: 'all',
    resolver: valibotResolver(schema),
    defaultValues
  });

export const useCurrentLanguage = (): ILanguage => {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const loginState = useSelectLoginState();
  const reduxLanguage = useAppSelector(state => state.user.preferences.language);

  const currentLanguage = () => {
    if (reduxLanguage) {
      const foundLanguage = supportedLanguages.find(
        supportedLanguage => supportedLanguage.code === reduxLanguage
      );

      if (foundLanguage) {
        return foundLanguage;
      }
    }

    const foundLanguage = supportedLanguages.find(
      supportedLanguage => supportedLanguage.code === i18n.language
    );

    if (foundLanguage) {
      return foundLanguage;
    }

    const defaultLanguage = supportedLanguages.find(
      supportedLanguage => supportedLanguage.code === appDefaultLanguageCode
    );

    return defaultLanguage ?? supportedLanguages[0];
  };

  useEffect(() => {
    const isCurrentLanguageSupported = supportedLanguages.some(
      supportedLanguage => supportedLanguage.code === i18n.language
    );

    if (!isCurrentLanguageSupported && i18n.language !== appDefaultLanguageCode) {
      const isLogged = loginState === LoginState.LOGGED;
      dispatch(saveLanguage({ languageCode: appDefaultLanguageCode, isLogged }));
    }
  }, [i18n, dispatch, loginState, reduxLanguage]);

  return currentLanguage();
};

export const useOutsideClick = (ref: RefObject<HTMLElement>, handler: (event: Event) => void) => {
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [ref, handler]);
};

export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      window.clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const useLocalStorage = <T>(
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
};

export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

type ImmediateIntervalCallback = () => void;

export const useImmediateInterval = (
  callback: ImmediateIntervalCallback,
  delay: number,
  clearingStatement = false
) => {
  const savedCallback = useRef<ImmediateIntervalCallback>(callback);

  useEffect(() => {
    callback();
  }, [callback]);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    const tick = () => {
      savedCallback.current?.();
    };

    if (delay !== null) {
      const id = window.setInterval(tick, delay);
      clearingStatement && window.clearInterval(id);
      return () => window.clearInterval(id);
    }

    return undefined;
  }, [delay, clearingStatement]);
};

type UseBoolean = [boolean, () => void, () => void];
export const useBoolean = (defaultValue = false): UseBoolean => {
  const [value, setValue] = useState(defaultValue);

  const setValueToTrue = () => {
    setValue(true);
  };

  const setValueToFalse = () => {
    setValue(false);
  };

  return [value, setValueToTrue, setValueToFalse];
};

export const useToggle = (initialValue = false): [boolean, () => void] => {
  const [value, setValue] = useState(initialValue);
  const toggle = () => setValue(currentValue => !currentValue);
  return [value, toggle];
};

export const useElementInFullscreen = (refElement: RefObject<HTMLDivElement>) => {
  const [isFullscreenEnabled, setFullscreenMode, setInitialMode] = useBoolean();

  const enterFullscreenMode = () => {
    refElement.current?.requestFullscreen();
  };

  const exitFullscreenMode = () => {
    document.exitFullscreen();
  };

  const toggleFullscreenMode = () => {
    isFullscreenEnabled ? exitFullscreenMode() : enterFullscreenMode();
  };

  const handleFullscreenChange = () => {
    if (document.fullscreenElement) {
      setFullscreenMode();
    } else {
      setInitialMode();
    }
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [handleFullscreenChange]);

  return {
    isFullscreenEnabled,
    enterFullscreenMode,
    exitFullscreenMode,
    toggleFullscreenMode
  };
};
